import { useEffect, useRef } from "react"
import { useCopilotChatInternal } from "@copilotkit/react-core"
import { useThreadContext } from "@/providers/ThreadProvider"
import { useOrderDraftStore } from "@/features/order-draft/store/order-draft.store"
import { readDraftText } from "@/features/oio/services/draft-storage.service"
import { apiClient } from "@/services/api.client"
import { serviceForThread } from "@/services/services.registry"

const POLL_INTERVAL_MS = 5_000

type RunStatusResponse = {
  status: string
  artifact_blob_url: string | null
  findings_blob_url: string | null
  error: string | null
}

export interface DraftReconnectOptions {
  /**
   * The `{agent_name}` of /api/v1/storage/runs/{agent_name}/{id}/status.
   * Defaults to the active thread's own agent (services.registry
   * `runsAgentName`), which is "oio" for every OIO and legacy thread.
   */
  agentName?: string
}

/**
 * Reconnect poller — the "close the tab / sleep the laptop / redeploy the
 * backend, reopen, and just see the result" path.
 *
 * Polls the oio_service run status endpoint (via the NitiAssist storage proxy)
 * while `draft_status` is a non-terminal in-flight value. When the run reaches
 * a terminal state (draft_completed / draft_failed_terminal) the hook adopts
 * the result: loads the artifact blob from GCS (if completed) and updates the
 * local thread state — without touching the NitiAssist DB.
 */
export function useDraftReconnect({ agentName }: DraftReconnectOptions = {}) {
  const { isLoading } = useCopilotChatInternal()
  const { activeThread, setActiveThread, refreshThreads } = useThreadContext()
  const { setContent } = useOrderDraftStore()
  const adoptedRef = useRef<string | null>(null)

  const id = activeThread?.id
  const metaStatus = activeThread?.metadata?.draft_status as string | undefined

  // Poll ONLY under the thread's own agent. The status route answers
  // `draft_not_started` for an agent that never ran on a thread, and this hook
  // WRITES the answer into the thread's draft_status. Polling an appeal as
  // "oio" would therefore reset a running OIA case to "not started" and bounce
  // the officer back to intake (frontend map G1). A caller passing a foreign
  // agent name gets no polling rather than a clobbered case.
  const ownAgentName = serviceForThread(activeThread).runsAgentName
  const pollAgentName = agentName ?? ownAgentName
  const pollsOwnAgent = pollAgentName === ownAgentName

  const shouldPoll =
    !!id &&
    !isLoading &&
    pollsOwnAgent &&
    (metaStatus === "draft_not_started" ||
      metaStatus === "draft_starting" ||
      metaStatus === "draft_running")

  useEffect(() => {
    if (!shouldPoll || !id || !activeThread) return

    let cancelled = false

    const loadBlob = async (blobUrl: string) => {
      // NOTE: this deliberately does NOT persist the draft to localStorage —
      // the old `oio-draft-${id}` write stored megabytes nothing read back,
      // slowed every synchronous getItem, and risked the 5MB quota (which
      // silently breaks the zustand persist). Reload cost is one blob fetch.
      try {
        const content = await readDraftText(blobUrl)
        if (!content || cancelled) return
        setContent(content)
      } catch {
        // non-fatal
      }
    }

    const poll = async () => {
      try {
        const fresh = await apiClient.get<RunStatusResponse>(
          `/api/v1/storage/runs/${pollAgentName}/${id}/status`,
        )
        if (cancelled || !fresh) return

        const freshStatus = fresh.status
        if (freshStatus === "draft_running") {
          if (metaStatus !== freshStatus) {
            setActiveThread({
              ...activeThread!,
              metadata: {
                ...(activeThread!.metadata ?? {}),
                draft_status: freshStatus,
              },
            })
          }
          return
        }

        if (freshStatus === "draft_not_started") {
          // Never overwrite an optimistic draft_starting or draft_running with draft_not_started
          // while a run is initiating.
          return
        }

        if (adoptedRef.current === id) return
        adoptedRef.current = id

        if (freshStatus === "draft_completed") {
          if (fresh.artifact_blob_url) {
            await loadBlob(fresh.artifact_blob_url)
          }
        }

        if (cancelled) return

        // Persist to DB so the terminal status survives page refresh.
        // setActiveThread is local-state only; refreshThreads() re-fetches from
        // the DB and would overwrite the optimistic local update if the DB still
        // has the old metadata. The PATCH uses JSONB || (merge), so only the
        // supplied keys are touched — existing thread metadata is preserved.
        // Omit null/undefined blob URLs from the PATCH to avoid clearing
        // existing blob references stored by a previous run.
        const dbPatch: Record<string, unknown> = { draft_status: freshStatus }
        if (fresh.artifact_blob_url) dbPatch.artifact_blob_url = fresh.artifact_blob_url
        if (fresh.findings_blob_url) dbPatch.findingsBlobUrl = fresh.findings_blob_url
        await apiClient
          .patch(`/api/v1/threads/${id}`, { metadata: dbPatch })
          .catch(() => {/* non-fatal — current session still works via local state */})

        setActiveThread({
          ...activeThread!,
          metadata: {
            ...(activeThread!.metadata ?? {}),
            draft_status: freshStatus,
            artifact_blob_url: fresh.artifact_blob_url || undefined,
            findingsBlobUrl: fresh.findings_blob_url || undefined,
          },
        })
        void refreshThreads()
      } catch {
        // transient network errors
      }
    }

    void poll()
    const timer = setInterval(() => void poll(), POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(timer)
    }
  }, [shouldPoll, id, setActiveThread, refreshThreads, setContent, activeThread, metaStatus, pollAgentName])
}
